import { useAuth } from "application/contexts/auth/AuthContext";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ScreenStore,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { UseBarCode } from "areas/barCode/UseBarCode";
import { IStartAction } from "components/startAction/StartAction";
import { SystemType } from "domains/kiosk/aggregates/Kiosk";
import { ChangeLangInput, useTranslation } from "i18n/useTranslation";
import { KioskRepository } from "Infrastructure/repositories/api/KioskRepository";
import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const kioskRepository = KioskRepository();

export const useHome = () => {
  const [resolution, orientation] = useScreenStore((state: ScreenStore) => [
    state.resolution,
    state.orientation,
  ]);
  const { kiosk } = useKiosk();
  const history = useHistory();
  const location = useLocation();
  const [clearCart] = useCartStore((state: ICartContext) => [state.clearCart]);
  const { clearCardData, disableSensor, isNominalData, isOnlineRecharge } =
    useCardReader();
  const { toast, showLoading, hideLoading } = useUi();
  const { verifySessionIsOpen } = useSession();
  const { addLog } = useAppInsights();
  const orderRepository = MeepOrderRepository(addLog);
  const { sync, catalog, handleRestrictions, handleDiscounts } =
    useCatalogStore();
  const { handleDisableIdleTimer, handleActiveIdleTimer } = useIdle();
  const { isAuth } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const { focus, refDiv } = UseBarCode();

  const [cpfRegisterOpen, setCpfRegisterOpen] = useState(false);
  const [showStartMenu, setSetshowStartMenu] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  const firstLoad = useRef(true);
  const closeModalClick = useRef(false);

  useEffect(() => {
    disableSensor();
  }, [disableSensor]);

  const openLanguageModal = useCallback(
    (event: React.MouseEvent<HTMLHeadingElement>) => {
      event.stopPropagation();
      setShowLanguageModal(true);
    },
    []
  );

  const closeLanguageModal = useCallback(() => {
    setShowLanguageModal(false);
  }, []);

  const handleChangeLanguage = useCallback(
    (event: React.MouseEvent<HTMLHeadingElement>, lng: ChangeLangInput) => {
      event.stopPropagation();
      changeLanguage(lng);
      setTimeout(() => {
        closeLanguageModal();
      }, 500);
    },
    [changeLanguage, closeLanguageModal]
  );

  const syncCatalog = useCallback(() => {
    if (kiosk && isAuth) {
      sync(kiosk.localId, kiosk.worksWithEvents ? kiosk?.eventId : undefined);
    }
  }, [isAuth, kiosk, sync]);

  const isSystemType = useCallback(
    (systemType: SystemType) => {
      return kiosk?.configSymstem?.find(
        (configSymstem) => configSymstem.systemType === systemType
      )
        ? true
        : false;
    },
    [kiosk]
  );

  let countClick = 0;
  const onClickLogo = useCallback(
    (event: React.MouseEvent<HTMLHeadingElement>) => {
      event.stopPropagation();
      let timer: NodeJS.Timer | null = null;
      if (countClick === 0) {
        timer = setTimeout(() => {
          if (countClick < 2) {
            fetch("//google.com", {
              mode: "no-cors",
            }).then(() => {
              window.location.reload();
            });
          }
          countClick = 0;
        }, 5000);
      } else if (countClick >= 4) {
        if (timer) {
          clearTimeout(timer);
        }
        history.push("/admin");
      }
      countClick++;

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    },
    [countClick, history]
  );

  const wallpaper = useMemo(
    () =>
      kiosk?.profilerNamePDV && kiosk?.wallpaperImageId
        ? `${process.env.REACT_APP_URL_IMAGEM}wallpapers/${kiosk.wallpaperImageId}.png`
        : kiosk?.wallpaperImageUrl,
    [kiosk]
  );

  // Ao voltar para a tela inicial ("/"), limpar restrições e descontos do catálogo
  useEffect(() => {
    if (location.pathname === "/") {
      handleRestrictions([]);
      handleDiscounts([]);
      clearCart();
      clearCardData();
    }
  }, [
    location.pathname,
    clearCardData,
    clearCart,
    handleDiscounts,
    handleRestrictions,
  ]);

  useEffect(() => {
    if (kiosk && firstLoad.current) {
      firstLoad.current = false;
      orderRepository.syncOrdersAsync(kiosk?.localId, kiosk?.id);
      kioskRepository.syncAsync();
    }
  }, [kiosk, orderRepository]);

  useEffect(() => {
    syncCatalog();
  }, [syncCatalog]);

  useEffect(() => {
    handleDisableIdleTimer();
  }, [handleDisableIdleTimer]);

  const verifySession = useCallback(async () => {
    showLoading();
    const result = await verifySessionIsOpen();
    hideLoading();
    return result;
  }, [hideLoading, showLoading, verifySessionIsOpen]);

  const goToMenu = useCallback(async () => {
    syncCatalog();
    clearCardData();
    const hasOpenSession = await verifySession();
    if (hasOpenSession) {
      if (catalog?.stores) {
        if (catalog.stores.length === 1) {
          history.push("/catalog/store/" + 0);
        } else {
          history.push("/catalog/");
        }
        setSetshowStartMenu(false);
      }
    }
  }, [catalog, clearCardData, history, syncCatalog, verifySession]);

  const goToNominalCashless = useCallback(async () => {
    clearCardData();
    const hasOpenSession = await verifySession();
    if (hasOpenSession) {
      isNominalData.current = true;
      history.push("/nominalCashless");
      setSetshowStartMenu(false);
    }
  }, [clearCardData, history, isNominalData, verifySession]);

  const goToPayment = useCallback(async () => {
    const hasOpenSession = await verifySession();
    if (hasOpenSession) {
      setSetshowStartMenu(false);
      history.push("/posPaid");
    }
  }, [history, verifySession]);

  const goToCashlessOnline = useCallback(async () => {
    clearCardData();
    const hasOpenSession = await verifySession();
    if (hasOpenSession) {
      isOnlineRecharge.current = true;
      setSetshowStartMenu(false);
      history.push("/prePaid");
    }
  }, [clearCardData, verifySession, isOnlineRecharge, history]);

  const goToCashlessOffline = useCallback(() => {
    if (!kiosk?.cashlessCode) {
      toast(t("offline_recharge.messages.activation_failed"), "error");
    } else {
      clearCardData();
      setSetshowStartMenu(false);
      history.push("/offlineRecharge");
    }
  }, [clearCardData, history, kiosk, toast, t]);

  const goToCashlessOfflineV2 = useCallback(() => {
    if (!kiosk?.cashlessCode) {
      toast(t("offline_recharge.messages.activation_failed"), "error");
    } else {
      clearCardData();
      setSetshowStartMenu(false);
      history.push("/offlineRechargeV2");
    }
  }, [clearCardData, history, kiosk, toast, t]);

  const goToWallet = useCallback(() => {
    clearCardData();
    setSetshowStartMenu(false);
    history.push("/wallet");
  }, [clearCardData, history]);

  const closeCpfRegister = useCallback(() => {
    closeModalClick.current = true;
    setCpfRegisterOpen(false);
  }, []);

  const goToGiveaway = useCallback(() => {
    closeModalClick.current = true;
    setSetshowStartMenu(false);
    setCpfRegisterOpen(true);
  }, []);

  const startActions = useMemo(() => {
    let _startActions: IStartAction[] = [];

    // Ir para o catalogo ou seleção de lojas
    if (isSystemType(SystemType.prepayTicket)) {
      const startAction: IStartAction = {
        icon: "CART",
        onClick: goToMenu,
        title: t("start_actions.menu_title"),
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: t("start_actions.menu_subtitle"),
      };
      _startActions.push(startAction);
    }

    // Ir para cashless nominal
    if (kiosk?.paymentNominalCashless) {
      const startAction: IStartAction = {
        icon: "CART",
        onClick: goToNominalCashless,
        title: t("start_actions.nominal_title"),
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: t("start_actions.menu_subtitle"),
      };
      _startActions.push(startAction);
    }

    // Ir para pagamento de contas
    if (
      isSystemType(SystemType.pospayOrderPad) &&
      !kiosk?.paymentNominalCashless
    ) {
      const startAction: IStartAction = {
        icon: "RECHARGE",
        onClick: goToPayment,
        title: t("start_actions.pay_account_title"),
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: t("start_actions.pay_account_subtitle"),
      };
      _startActions.push(startAction);
    }

    // Ir para Cashless Online
    if (
      ((isSystemType(SystemType.prepayCashless) &&
        kiosk?.onlineCashlessControl) ||
        kiosk?.paymentNominalCashless) &&
      kiosk?.enableRecharge
    ) {
      const startAction: IStartAction = {
        icon: "RECHARGE",
        onClick: goToCashlessOnline,
        title: t("start_actions.recharge_title"),
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: t("start_actions.recharge_subtitle"),
      };
      _startActions.push(startAction);
    }

    // Ir para Cashless Offline (comentado – usando apenas fluxo V2)
    // if (
    //   isSystemType(SystemType.prepayCashless) &&
    //   !kiosk?.onlineCashlessControl &&
    //   !kiosk?.paymentNominalCashless &&
    //   kiosk?.enableRecharge
    // ) {
    //   const startAction: IStartAction = {
    //     icon: "RECHARGE",
    //     onClick: goToCashlessOffline,
    //     title: t("start_actions.recharge_title"),
    //     color: kiosk?.themeColor ?? kiosk?.primaryColor,
    //     subTitle: t("start_actions.recharge_subtitle"),
    //   };
    //   _startActions.push(startAction);

      // Novo fluxo de Recarga Cashless Offline (V2)
      if (
        isSystemType(SystemType.prepayCashless) &&
        !kiosk?.onlineCashlessControl &&
        !kiosk?.paymentNominalCashless &&
        kiosk?.enableRecharge
      ) {
        const startActionOfflineV2: IStartAction = {
          icon: "RECHARGE",
          onClick: goToCashlessOfflineV2,
          title: t("start_actions.recharge_title"),
          color: kiosk?.themeColor ?? kiosk?.primaryColor,
          subTitle: t("start_actions.recharge_subtitle"),
        };
        _startActions.push(startActionOfflineV2);
      }
    // }

    // Ir para Wallet
    if (isSystemType(SystemType.wallet) && kiosk?.enableWalletRecharge) {
      const startAction: IStartAction = {
        icon: "CARD", //TODO: Pegar icone com higu
        onClick: goToWallet,
        title: "Wallet",
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: "Recarregue sua carteira",
      };
      // _startActions.push(startAction); TODO: Adicionar quando revisar fluxo Wallet
    }

    // Ir para Sorteio
    if (kiosk?.enableCpfRegister) {
      const startAction: IStartAction = {
        icon: "CART", //TODO: Pegar icone com higu
        onClick: goToGiveaway,
        title: "SORTEIO",
        color: kiosk?.themeColor ?? kiosk?.primaryColor,
        subTitle: "Seja bem-vindo e boa sorte 🤞",
      };
      // _startActions.push(startAction); TODO: Revisar a inclusão
    }

    return _startActions;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kiosk,
    isSystemType,
    goToMenu,
    goToNominalCashless,
    goToPayment,
    goToCashlessOnline,
    goToCashlessOffline,
    goToCashlessOfflineV2,
    goToWallet,
    goToGiveaway,
    t,
    language
  ]);

  const handleClickStart = useCallback(() => {
    if (kiosk?.barCodeMode) {
      focus();
      return;
    }
    handleActiveIdleTimer();
    if (closeModalClick.current) {
      closeModalClick.current = false;
      return;
    }

    if (startActions.length === 0) {
      return;
    }

    if (startActions.length === 1) {
      startActions[0].onClick();
    } else {
      setSetshowStartMenu(true);
    }
  }, [focus, handleActiveIdleTimer, kiosk, startActions]);

  const handleCloseMenu = useCallback(() => {
    handleDisableIdleTimer();
    closeModalClick.current = true;
    setSetshowStartMenu(false);
  }, [handleDisableIdleTimer]);

  return {
    wallpaper,
    onClickLogo,
    kiosk,
    startActions,
    handleClickStart,
    showStartMenu,
    handleCloseMenu,
    resolution,
    orientation,
    cpfRegisterOpen,
    closeCpfRegister,
    showLanguageModal,
    t,
    closeLanguageModal,
    openLanguageModal,
    handleChangeLanguage,
    language,
    focus,
    refDiv,
  };
};
